import { GetStaticPaths, GetStaticProps } from 'next';
import { useContext } from 'react';
import { useRouter } from 'next/router';
import { QueryClient } from 'react-query';
import { dehydrate } from 'react-query/hydration';
import { LabelList } from 'src/app/label/label-list';
import { UserProfile } from 'src/app/profile/types';
import useProfileQuery from 'src/app/profile/use-profile-query';
import { StudioLayout } from 'src/app/studio/layout';
import { UserContext, UserContextType } from 'src/contexts/UserContext';
import {
  getUsernameFromParams,
  prefetchProfile,
} from 'src/app/studio/prefetch';
import { useIsMobile } from 'src/hooks/use-is-mobile';

function Collections({ username }: { username: string }) {
  const { data: profile } = useProfileQuery(username);

  const { user } = useContext(UserContext) as UserContextType;

  const isMobile = useIsMobile();

  const router = useRouter();

  if (router.isFallback || !profile) {
    return null;
  }

  return (
    <LabelList
      isOwner={user && user.id === profile.id}
      req={{
        areaID: String(profile.id),
        areaType: 'STUDIO',
        labelType: 'COLLECTION',
      }}
      isMobile={isMobile}
    />
  );
}

Collections.Layout = StudioLayout;

export const getStaticProps: GetStaticProps = async ({ params }) => {
  const username = getUsernameFromParams({ params });
  const queryClient = new QueryClient();

  let profile: UserProfile;

  try {
    profile = await prefetchProfile(queryClient, username);
  } catch (error) {
    return {
      redirect: {
        destination: '/',
        statusCode: 404,
      },
      props: {
        username,
      },
    };
  }

  if (!profile) {
    return {
      notFound: true,
      props: {
        username,
      },
    };
  }

  return {
    props: {
      username,
      userID: profile.id,
      dehydratedState: dehydrate(queryClient),
    },
    revalidate: 100,
  };
};

export const getStaticPaths: GetStaticPaths = async () => {
  return {
    fallback: 'blocking', // before we have done cover and loading state
    paths: [],
  };
};

export default Collections;
